import React, { lazy, Suspense, useEffect } from "react";

import { observer } from "mobx-react-lite";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  Routes,
  useOutletContext,
} from "react-router-dom";

import { AgentHistoryPage } from "src/agents/AgentHistoryPage";
import { AgentPage } from "src/agents/AgentPage";
import { AgentsPage } from "src/agents/AgentsPage";
import { AgentVersionPage } from "src/agents/AgentVersionPage";
import { AgentVersionsPage } from "src/agents/AgentVersionsPage";
import { WorkspaceInsightsContent } from "src/analytics/workspace/WorkspaceInsightsContent";
import { WorkspaceInsightsRouteGuard } from "src/analytics/workspace/WorkspaceInsightsRouteGuard";
import { Flow, FlowVersion } from "src/api/flowTypes";
import { useFlowVersion } from "src/api/flowVersionQueries";
import { baseUrl } from "src/api/hosts";
import { useModules } from "src/api/modulesApi";
import {
  useAccessibleOrganizations,
  useFlow,
  useNavigableWorkspaces,
  useWorkspace as useWorkspaceV1,
} from "src/api/queries";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { LoadingView } from "src/base-components/LoadingView";
import { ResizableSidepaneProvider } from "src/base-components/ResizableSidepane";
import { RoleOverrideIndicator } from "src/base-components/RoleOverrideIndicator";
import { Spinner } from "src/base-components/Spinner";
import { CaseAnalyticsContent } from "src/cases/analytics/CaseAnalyticsContent";
import { CaseAnalyticsRouteGuard } from "src/cases/analytics/CaseAnalyticsRouteGuard";
import { useCaseAnalyticsAccess } from "src/cases/analytics/useCaseAnalyticsAccess";
import { CaseDashboard } from "src/cases/CaseDashboard";
import { CaseQueueConfigurationPage } from "src/cases/CaseQueueConfigurationPage";
import { CaseQueuesPage } from "src/cases/CaseQueuesPage";
import { CaseTemplatesPage } from "src/cases/CaseTemplatesPage";
import { CaseTemplatesRedirect } from "src/cases/CaseTemplatesRedirect";
import { CaseTemplateEditPage } from "src/cases/CaseTemplateVersionPage";
import { CaseTemplateVersionsPage } from "src/cases/CaseTemplateVersionsPage";
import { CaseViewPage } from "src/cases/CaseViewPage";
import { CreateCasePage } from "src/cases/CreateCasePage";
import { useRevision } from "src/changeHistory/queries";
import { SimpleRoleType } from "src/clients/taktile-api";
import { DecisionsOverview } from "src/decisionsOverview/DecisionsOverview";
import { Entities } from "src/entities/Entities";
import { EntityViewWrapper } from "src/entities/entityView/EntityView";
import { EntityTabIds } from "src/entities/entityView/useEntityTabRefresh";
import { EventSchemaEditorSidebar } from "src/eventsCatalogue/EventSchemaEditorSidebar";
import { EventsCatalogue } from "src/eventsCatalogue/EventsCatalogue";
import { EventsCatalogueIndex } from "src/eventsCatalogue/EventsCatalogueIndex";
import { EventViewSidebar } from "src/eventsCatalogue/EventViewSidebar";
import { FeaturesPage } from "src/featureCatalogue/Features/FeaturesPage";
import { ManageFeatureQueryPage } from "src/featureCatalogue/Queries/ManageFeatureQueryPage";
import { QueriesPage } from "src/featureCatalogue/Queries/QueriesPage";
import { FlowAnalyticsContent } from "src/flow/analytics/FlowAnalyticsContent";
import { LegacyAnalyticsPathRedirect } from "src/flow/analytics/LegacyAnalyticsPathRedirect";
import { LiveAnalyticsRouteGuard } from "src/flow/analytics/LiveAnalyticsRouteGuard";
import { FlowHistoryContent } from "src/flow/decisionHistory/FlowHistoryContent";
import {
  FLOW_DECISION_HISTORY_UNAUTHORIZED_MESSAGE,
  WORKSPACE_DECISION_HISTORY_UNAUTHORIZED_MESSAGE,
} from "src/flow/decisionHistory/SharedEmptyStateWording";
import { FlowPage } from "src/flow/FlowPage";
import { FlowTrafficContent } from "src/flow/FlowTrafficContent";
import { FlowVersionsContent } from "src/flow/FlowVersionsContent";
import { NavigateToFlowVersion } from "src/flowContainer/NavigateToFlowVersion";
import { VersionAuthoringWrapper } from "src/flowContainer/VersionAuthoringWrapper";
import { FlowOverview } from "src/flowsOverview/FlowOverview";
import { GitbookAuthPage } from "src/gitbookAuth/GitbookAuthPage";
import { ANY_FOLDER } from "src/hooks/useCapabilities/types";
import { useIsByokEnabled } from "src/hooks/useIsByokEnabled";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import { useIsModuleEnabled } from "src/hooks/useIsModuleEnabled";
import { useIsTaktileOrgUser } from "src/hooks/useIsTaktileOrgUser";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { JobContent } from "src/jobs/JobContent";
import { JobsContent } from "src/jobs/JobsContent";
import { JobRun } from "src/jobs/types";
import { MainPageDashboard } from "src/mainPage/MainPage";
import { ReviewCaseContent } from "src/manualReview/ReviewCaseContent";
import { ReviewCasePreviewContent } from "src/manualReview/ReviewCasePreviewContent";
import { ReviewQueueContent } from "src/manualReview/ReviewQueueContent";
import { OAuthConsentPage } from "src/oauth/OAuthConsentPage";
import { OmniboxOther } from "src/omnibox/OmniboxOther";
import { OutcomesContent } from "src/outcomes/OutcomesContent";
import { PerformanceRouteGuard } from "src/performance/PerformanceRouteGuard";
import { PerformanceRouter } from "src/performance/PerformanceRouter";
import { DisplayRedirects } from "src/router/DisplayRedirects";
import { OrgFavicon } from "src/router/DynamicFavicon";
import { ErrorPage } from "src/router/ErrorPage";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { PartnerStorageManager } from "src/router/PartnerStorageManager";
import { PickWorkspace } from "src/router/PickWorkspace";
import { ProtectedFlowPage } from "src/router/ProtectedFlowPage";
import { ProtectedFlowRoute } from "src/router/ProtectedFlowRoute";
import { ProtectedPage } from "src/router/ProtectedPage";
import { ProtectedRoute } from "src/router/ProtectedRoute";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { PageViewTracker } from "src/router/Track";
import { UnscopedSession } from "src/router/UnscopedSession";
import {
  AuthorPageParams,
  adminPanelPath,
  DashboardPageParams,
  getUrlToSettingsPage,
  getUrlToWsDashboard,
  getWelcomeUrl,
  RevisionPageParams,
  SimulationPageParams,
  WsDashboardPageCode,
} from "src/router/urls";
import { WorkspaceScoping } from "src/router/WorkspaceScoping";
import { SessionDebugPage } from "src/sessionDebug/SessionDebugPage";
import { SettingsPage } from "src/settings/SettingsPage";
import { SettingsPageRedirect } from "src/settings/SettingsPageRedirect";
import { SettingsSubpages } from "src/settings/SettingsSubpages";
import { canAccessSettingsPage } from "src/settings/utils";
import { useSimulationRun } from "src/simulations/useSimulationRun";
import { useIsUserRefreshed } from "src/store/AuthStore";
import { SimpleRoleProvider } from "src/store/SimpleRoleProvider";
import { ThemeProvider } from "src/styles/themes/ThemeProvider";
import { CopilotContextProvider } from "src/unifiedCopilot/context/CopilotProvider";
import { UnifiedCopilot } from "src/unifiedCopilot/UnifiedCopilot";
import { UnifiedCopilotPage } from "src/unifiedCopilot/UnifiedCopilotPage";
import { useIsUnifiedCopilotEnabled } from "src/unifiedCopilot/useIsUnifiedCopilotEnabled";
import { WelcomePage } from "src/userManagement/WelcomePage";
import { ErrorBoundary } from "src/utils/ErrorBoundary";
import { useBootWebchat } from "src/utils/useBootWebchat";
import { useNavigableWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";
import { useLastWorkspaceId } from "src/utils/useLastWorkspaceId";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { OutgoingWebhooksPage } from "src/webhooks/WebhooksPage";

const DocsPage = lazy(() => import("src/docs/DocsPage"));
// Taktile-internal page: keep it out of the entry chunk so customers never
// download it.
const Evals = lazy(() =>
  import("src/evals/EvalsPage").then((module) => ({
    default: module.EvalsPage,
  })),
);
const Retool = lazy(() =>
  import("src/retool/Retool").then((module) => ({ default: module.Retool })),
);
// Taktile-internal page: keep it out of the entry chunk so customers never
// download it.
const AdminPanel = lazy(() =>
  import("src/adminPanel/AdminPanel").then((module) => ({
    default: module.AdminPanel,
  })),
);

enum ORG_ROUTE_CODES {
  UsersPermissions = "users-permissions",
}

const FlowVersionRoutes: React.FC = () => {
  const { workspace, modules } = useWorkspaceContext();
  const { flow_id, version_id, orgId } = useParamsDecode<AuthorPageParams>();

  // Fetching flow and flow version here allows to disable refetchOnMount on all child components
  // but still able fetch latest data on initial page visit while navigating within the app
  // const workspaceQuery = useWorkspace(wsId);
  const flowQuery = useFlow(flow_id, { refetchOnMount: true });
  const versionQuery = useFlowVersion(version_id, true);
  const isRevision = false;

  return (
    <LoadingView
      queryResult={[flowQuery, versionQuery]}
      renderUpdated={([flow, version]: [Flow, FlowVersion]) => (
        <ProtectedPage
          canAccess={(c) => c.flowVersions.canAccess(flow.flow_folder_id)}
        >
          <Outlet
            context={{
              workspace,
              modules,
              flow,
              version,
              orgId,
              isRevision,
            }}
          />
        </ProtectedPage>
      )}
    />
  );
};

const WorkspaceRoutesContext: React.FC = () => {
  const { wsId, orgId } = useParamsDecode<DashboardPageParams>();
  const { data: workspace, isLoading, isError } = useWorkspaceV1(wsId);
  const {
    data: modules,
    isLoading: isModulesLoading,
    isError: isModulesError,
  } = useModules({ workspaceId: wsId });

  if (isLoading || isModulesLoading) {
    return <Spinner />;
  }

  if (isError || isModulesError) {
    return (
      <ErrorPage message="You do not have access to the requested organization or workspace. Please contact Taktile support if you think this is an error." />
    );
  }

  return <Outlet context={{ workspace, modules, orgId }} />;
};

const FlowVersionRevisionRoutes: React.FC = () => {
  const { workspace, modules, orgId } = useWorkspaceContext();
  const { flow_id, version_id, revision_etag } =
    useParamsDecode<RevisionPageParams>();

  // Fetching flow and flow version here allows to disable refetchOnMount on all child components
  // but still able fetch latest data on initial page visit while navigating within the app
  const flowQuery = useFlow(flow_id, { refetchOnMount: true });
  const versionQuery = useRevision({
    versionId: version_id,
    etag: revision_etag,
  });
  const isRevision = true;

  return (
    <LoadingView
      queryResult={[flowQuery, versionQuery]}
      renderUpdated={([flow, version]: [Flow, FlowVersion]) => (
        <ProtectedPage
          canAccess={(c) => c.flowVersions.canAccess(flow.flow_folder_id)}
        >
          <Outlet
            context={{
              workspace,
              modules,
              flow,
              version,
              orgId,
              isRevision,
            }}
          />
        </ProtectedPage>
      )}
    />
  );
};

const SimulationVersionRoutes: React.FC = () => {
  const { workspace, modules, orgId } = useWorkspaceContext();
  const { flow_id, version_id, simulationId } =
    useParamsDecode<SimulationPageParams>();

  // Fetching flow and flow version here allows to disable refetchOnMount on all child components
  // but still able fetch latest data on initial page visit while navigating within the app
  const flowQuery = useFlow(flow_id, { refetchOnMount: true });
  const versionQuery = useFlowVersion(version_id, true);
  const simulationQuery = useSimulationRun(flow_id, version_id, simulationId);

  const isRevision = false;
  const isSimulation = true;

  return (
    <LoadingView
      queryResult={[flowQuery, versionQuery, simulationQuery]}
      renderErrored={(message) => <ErrorPage message={message} />}
      renderUpdated={([flow, version, _simulation]: [
        Flow,
        FlowVersion,
        JobRun,
      ]) => (
        <ProtectedPage
          canAccess={(c) => c.flowVersions.canAccess(flow.flow_folder_id)}
        >
          <Outlet
            context={{
              workspace,
              modules,
              flow,
              version,
              orgId,
              isRevision,
              isSimulation,
              simulationId,
            }}
          />
        </ProtectedPage>
      )}
    />
  );
};

const FlowRoutes = (
  <>
    <Route element={<FlowPage />} path="flows/:flow_id/">
      <Route
        element={
          <ProtectedFlowPage
            canAccess={(c, folder) => c.flowVersions.canAccess(folder)}
          >
            <FlowVersionsContent />
          </ProtectedFlowPage>
        }
        index
      />

      <Route
        element={
          <ProtectedFlowPage
            canAccess={(c, folder) => c.flowVersions.canAccess(folder)}
          >
            <FlowTrafficContent />
          </ProtectedFlowPage>
        }
        path="traffic"
      />
      <Route
        element={
          <ProtectedFlowPage
            canAccess={(c, folder) => c.decisionHistory.canAccess(folder)}
            unauthorizedMessage={FLOW_DECISION_HISTORY_UNAUTHORIZED_MESSAGE}
          >
            <FlowHistoryContent />
          </ProtectedFlowPage>
        }
        path="history"
      />
      <Route
        element={
          <ProtectedPage canAccess={(c) => c.outgoingWebhooks.canAccess}>
            <OutgoingWebhooksPage />
          </ProtectedPage>
        }
        path="webhooks"
      />
      <Route
        element={
          <ProtectedFlowPage canAccess={(c, folder) => c.flows.canEdit(folder)}>
            <PerformanceRouteGuard>
              <PerformanceRouter />
            </PerformanceRouteGuard>
          </ProtectedFlowPage>
        }
        path="performance"
      />
      <Route
        element={<ProtectedRoute canAccess={(c) => c.manualReview.canAccess} />}
        path="review-queue"
      >
        <Route element={<ReviewQueueContent />} index />
        <Route element={<ReviewCaseContent />} path=":case_id" />
      </Route>
      <Route
        element={
          <ProtectedFlowRoute
            canAccess={(c, folder) => c.jobs.canAccess(folder)}
          />
        }
        path="jobs"
      >
        <Route element={<JobsContent />} index />
        <Route element={<JobContent />} path=":job_id" />
      </Route>
      <Route
        element={<ProtectedRoute canAccess={(c) => c.outcomes.canAccess} />}
        path="outcomes"
      >
        <Route element={<OutcomesContent />} index />
      </Route>
      <Route
        element={
          <LiveAnalyticsRouteGuard>
            <ProtectedFlowPage
              canAccess={(c, folder) => c.flows.canEdit(folder)}
            >
              <FlowAnalyticsContent />
            </ProtectedFlowPage>
          </LiveAnalyticsRouteGuard>
        }
        path="insights"
      />
    </Route>
    <Route
      element={<LegacyAnalyticsPathRedirect />}
      path="flows/:flow_id/analytics"
    />
  </>
);

const WorkspaceRoutes: React.FC = observer(() => {
  const { wsId, orgId } = useParamsDecode<DashboardPageParams>();
  const [, setLastWorkspaceId] = useLastWorkspaceId();
  const welcomeUrl = getWelcomeUrl(orgId);
  const { workspace } = useWorkspaceContext();
  const featureGates = useWorkspaceFeatureGates();
  const isByokEnabled = useIsByokEnabled();
  const isOauthClientsEnabled = useIsFeatureFlagEnabled(
    FEATURE_FLAGS.oauthClients,
  );
  const {
    org: organization,
    orgs,
    isLoading: isOrgsLoading,
  } = useNavigableWorkspaceAndOrg();
  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();
  const { canAccess: canAccessCaseAnalytics } = useCaseAnalyticsAccess();
  const isTaktileUser = useIsTaktileOrgUser();
  const { isEnabled: isEvalsEnabled } = useIsModuleEnabled("evals");
  const { isEnabled: isUnifiedCopilotEnabled } = useIsUnifiedCopilotEnabled();

  useEffect(() => {
    setLastWorkspaceId(wsId);
  }, [setLastWorkspaceId, wsId]);

  useBootWebchat({
    enabled: workspace?.config?.enable_customer_support_widget ?? true,
  });

  if (!workspace || (!isOrgsLoading && orgs.length > 0 && !organization)) {
    return (
      <Routes>
        <Route
          element={
            <ErrorPage message="You do not have access to the requested organization or workspace. Please contact Taktile support if you think this is an error." />
          }
          path="*"
        />
      </Routes>
    );
  }

  return (
    <SimpleRoleProvider>
      {/* resetKey keeps the contextual-entity registry workspace-scoped
          without remounting the route tree. */}
      <CopilotContextProvider resetKey={wsId}>
        <PartnerStorageManager />
        <OrgFavicon />
        <OmniboxOther />
        <RoleOverrideIndicator />
        <UnifiedCopilot key={wsId}>
          <Routes>
            <Route
              element={<Navigate to={getUrlToWsDashboard({ orgId, wsId })} />}
              index
            />

            <Route
              element={<ProtectedRoute canAccess={canAccessSettingsPage} />}
              path="settings"
            >
              <Route
                element={
                  <ProtectedPage
                    canAccess={(c) => c.usersPermissions.canAccess}
                  >
                    <SettingsPage subpage={SettingsSubpages.Users} />
                  </ProtectedPage>
                }
                path="users"
              />
              {isFeatureFlagEnabled(FEATURE_FLAGS.granularPermissions) && (
                <Route
                  element={
                    <ProtectedPage
                      canAccess={(c) => c.usersPermissions.canAccess}
                    >
                      <SettingsPage
                        subpage={SettingsSubpages.UserPermissions}
                      />
                    </ProtectedPage>
                  }
                  path="users/:userId"
                />
              )}
              <Route
                element={
                  <ProtectedPage canAccess={(c) => c.connections.canAccess}>
                    <SettingsPage subpage={SettingsSubpages.Connections} />
                  </ProtectedPage>
                }
                path="connections"
              />
              <Route
                element={
                  <ProtectedPage canAccess={(c) => c.apiKeys.canAccess}>
                    <SettingsPage subpage={SettingsSubpages.ApiKeys} />
                  </ProtectedPage>
                }
                path="api-keys"
              />
              {isOauthClientsEnabled && (
                <Route
                  element={
                    <ProtectedPage canAccess={(c) => c.apiKeys.canAccess}>
                      <SettingsPage subpage={SettingsSubpages.OauthClients} />
                    </ProtectedPage>
                  }
                  path="oauth-clients"
                />
              )}
              {isByokEnabled && (
                <Route
                  element={
                    <ProtectedPage canAccess={(c) => c.apiKeys.canAccess}>
                      <SettingsPage
                        subpage={SettingsSubpages.AgentCredentials}
                      />
                    </ProtectedPage>
                  }
                  path="agent-credentials"
                />
              )}
              <Route
                element={
                  <ProtectedPage canAccess={(c) => c.aiUsageLimits.canAccess}>
                    <SettingsPage subpage={SettingsSubpages.AiUsageLimits} />
                  </ProtectedPage>
                }
                path="ai-usage-limits"
              />
              <Route
                element={
                  <ProtectedPage canAccess={(c) => c.auditLogs.canAccess}>
                    <SettingsPage subpage={SettingsSubpages.AuditLogs} />
                  </ProtectedPage>
                }
                path="audit-logs"
              />
              <Route
                element={
                  <ProtectedPage
                    canAccess={(c) => c.role === SimpleRoleType.ADMIN}
                  >
                    <SettingsPage subpage={SettingsSubpages.Powertools} />
                  </ProtectedPage>
                }
                path="powertools"
              />
              <Route
                element={
                  <ProtectedPage
                    canAccess={(c) => c.role === SimpleRoleType.ADMIN}
                  >
                    <SettingsPage subpage={SettingsSubpages.Sessions} />
                  </ProtectedPage>
                }
                path="sessions"
              />
              <Route element={<SettingsPageRedirect />} index />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  canAccess={(c) => c.workspaces.canAccess}
                  redirectPath={welcomeUrl}
                />
              }
            >
              <Route element={<MainPageDashboard />}>
                <Route element={<></>} path={WsDashboardPageCode.Dashboard} />
                <Route
                  element={
                    <FlowOverview organizationId={orgId} workspaceId={wsId} />
                  }
                  path={WsDashboardPageCode.Flows}
                />
                {featureGates.entitiesEnabled && (
                  <Route path={WsDashboardPageCode.Entities}>
                    <Route element={<Entities />} index />
                    <Route element={<Entities />} path=":schema" />
                    {Object.values(EntityTabIds).map((tab) => (
                      <Route
                        key={tab}
                        element={<EntityViewWrapper tab={tab} />}
                        path={`:schema/:id/${tab}`}
                      />
                    ))}
                  </Route>
                )}
                {featureGates.featuresEventsEnabled && (
                  <Route path={WsDashboardPageCode.Events}>
                    <Route element={<EventsCatalogueIndex />} index />
                    <Route element={<EventsCatalogue />} path=":event_type">
                      <Route
                        element={
                          <ResizableSidepaneProvider>
                            <EventViewSidebar />
                          </ResizableSidepaneProvider>
                        }
                        path="event/:event_id"
                      />
                      <Route
                        element={<EventSchemaEditorSidebar />}
                        path="edit"
                      />
                    </Route>
                  </Route>
                )}
                {featureGates.featuresEventsEnabled && (
                  <Route path={WsDashboardPageCode.Features}>
                    <Route
                      element={
                        <ProtectedPage
                          canAccess={(c) => c.featureCatalogue.canAccess}
                        >
                          <FeaturesPage />
                        </ProtectedPage>
                      }
                      index
                    />
                    <Route
                      element={
                        <ProtectedPage
                          canAccess={(c) => c.featureQueriesCatalogue.canAccess}
                        >
                          <QueriesPage />
                        </ProtectedPage>
                      }
                      path="queries"
                    />
                  </Route>
                )}
                {isTaktileUser && isEvalsEnabled && (
                  <Route
                    element={
                      <Suspense fallback={<Spinner />}>
                        <Evals />
                      </Suspense>
                    }
                    path={WsDashboardPageCode.Evals}
                  />
                )}
              </Route>
              {featureGates.featuresEventsEnabled && (
                <Route
                  element={
                    <ProtectedPage
                      canAccess={(c) => c.featureQueriesCatalogue.canAccess}
                    >
                      <ManageFeatureQueryPage />
                    </ProtectedPage>
                  }
                  path="features/queries/manage"
                />
              )}
              <Route
                element={
                  <ProtectedPage
                    canAccess={(c) => c.decisionHistory.canAccess(ANY_FOLDER)}
                    unauthorizedMessage={
                      WORKSPACE_DECISION_HISTORY_UNAUTHORIZED_MESSAGE
                    }
                  >
                    <ResizableSidepaneProvider>
                      <DecisionsOverview />
                    </ResizableSidepaneProvider>
                  </ProtectedPage>
                }
                path="decisions"
              />
              <Route
                element={<NavigateToUsersPermissions />}
                path={ORG_ROUTE_CODES.UsersPermissions}
              />
              {FlowRoutes}
            </Route>

            {/* Case management routes */}
            <Route
              element={
                <ProtectedRoute
                  canAccess={() => isCaseManagementEnabled}
                  redirectPath={getUrlToWsDashboard({ orgId, wsId })}
                />
              }
              path="cases"
            >
              <Route element={<CaseDashboard />}>
                <Route
                  element={
                    <CaseAnalyticsRouteGuard>
                      <ProtectedPage
                        canAccess={(c) => c.caseAnalytics.canAccess}
                      >
                        <CaseAnalyticsContent />
                      </ProtectedPage>
                    </CaseAnalyticsRouteGuard>
                  }
                  path="analytics"
                />

                <Route
                  element={
                    <ProtectedPage canAccess={(c) => c.cases.canAccess}>
                      <CaseQueuesPage />
                    </ProtectedPage>
                  }
                  index
                />

                <Route path="templates">
                  <Route
                    element={
                      <ProtectedPage
                        canAccess={(c) => c.caseTemplates.canAccess}
                      >
                        {canAccessCaseAnalytics ? (
                          <CaseTemplatesRedirect />
                        ) : (
                          <CaseTemplatesPage />
                        )}
                      </ProtectedPage>
                    }
                    index
                  />
                  <Route
                    element={<CaseTemplateVersionsPage />}
                    path=":templateId/versions"
                  />
                </Route>

                <Route path="queues">
                  <Route
                    element={
                      <ProtectedPage canAccess={(c) => c.cases.canAccess}>
                        <CaseQueuesPage />
                      </ProtectedPage>
                    }
                    path=":queue_id"
                  />
                </Route>
              </Route>

              <Route
                element={
                  <ProtectedPage canAccess={(c) => c.cases.canAccess}>
                    <CaseViewPage />
                  </ProtectedPage>
                }
                path=":case_id"
              />

              <Route path="queues">
                <Route
                  element={
                    <ProtectedPage canAccess={(c) => c.caseQueues.canCreate}>
                      <CaseQueueConfigurationPage />
                    </ProtectedPage>
                  }
                  path="new"
                />

                <Route
                  element={
                    <ProtectedPage canAccess={(c) => c.caseQueues.canEdit}>
                      <CaseQueueConfigurationPage />
                    </ProtectedPage>
                  }
                  path=":queue_id/edit"
                />

                <Route
                  element={
                    <ProtectedPage canAccess={(c) => c.cases.canCreate}>
                      <CreateCasePage />
                    </ProtectedPage>
                  }
                  path=":queue_id/cases/create"
                />
              </Route>

              <Route path="templates">
                <Route
                  element={
                    <ProtectedPage canAccess={(c) => c.caseTemplates.canAccess}>
                      <CaseTemplateEditPage />
                    </ProtectedPage>
                  }
                  path=":templateId/versions/:versionId"
                />
              </Route>
            </Route>
            {isUnifiedCopilotEnabled && (
              <Route element={<MainPageDashboard />} path="copilot">
                <Route element={<UnifiedCopilotPage />} index />
              </Route>
            )}
            <Route
              element={
                <WorkspaceInsightsRouteGuard>
                  <MainPageDashboard />
                </WorkspaceInsightsRouteGuard>
              }
              path="insights"
            >
              <Route
                element={
                  <ProtectedPage
                    canAccess={(c) => c.workspaceInsights.canAccess}
                  >
                    <WorkspaceInsightsContent />
                  </ProtectedPage>
                }
                index
              />
            </Route>
            {(featureGates.agentsEnabled || featureGates.isLoading) && (
              <Route
                element={
                  <ProtectedPage canAccess={(c) => c.agents.canAccess}>
                    <MainPageDashboard />
                  </ProtectedPage>
                }
                path="agents"
              >
                <Route element={<AgentsPage />} index />
                <Route element={<AgentRoutesLayout />} path=":agentId">
                  <Route element={<AgentPage />}>
                    <Route element={<AgentVersionsPage />} path="versions" />
                    <Route element={<AgentHistoryPage />} path="history" />
                  </Route>
                  <Route
                    element={<AgentVersionPage />}
                    path="versions/:versionId"
                  />
                </Route>
              </Route>
            )}
            <Route element={<MainPageDashboard />} path="agents/debug">
              <Route element={<SessionDebugPage />} path="sessions" />
            </Route>
            <Route element={<NavigateToFlowVersion />} path="flow/:flow_id/" />
            <Route
              element={
                <ProtectedRoute
                  canAccess={(c) => c.flowVersions.canAccess(ANY_FOLDER)}
                />
              }
            >
              <Route
                element={<FlowVersionRoutes />}
                path="flow/:flow_id/version/:version_id/"
              >
                <Route element={<VersionAuthoringWrapper />} index />
                <Route
                  element={
                    <Suspense fallback={<Spinner />}>
                      <DocsPage />
                    </Suspense>
                  }
                  path="docs"
                />
              </Route>

              <Route
                element={<FlowVersionRevisionRoutes />}
                path="flow/:flow_id/version/:version_id/revision/:revision_etag"
              >
                <Route element={<VersionAuthoringWrapper />} index />
              </Route>

              <Route
                element={
                  <ProtectedPage canAccess={(c) => c.simulations.canAccess}>
                    <SimulationVersionRoutes />
                  </ProtectedPage>
                }
                path="flow/:flow_id/version/:version_id/simulation/:simulationId"
              >
                <Route element={<VersionAuthoringWrapper />} index />
              </Route>
            </Route>
            <Route
              element={
                <ProtectedRoute
                  canAccess={(c) => c.manualReview.canAccess}
                  redirectPath={welcomeUrl}
                />
              }
            >
              {/**
               * This route is outside of DashboardLayout,
               * because we don't want it to have the main navigation sidebar.
               * It is just manual review case preview
               */}
              <Route
                element={<ReviewCasePreviewContent />}
                path="flows/:flow_id/review-queue/preview"
              />

              <Route
                element={
                  <ProtectedPage canAccess={(c) => c.manualReview.canAccess}>
                    <Suspense fallback={<Spinner />}>
                      <Retool wsId={wsId} />
                    </Suspense>
                  </ProtectedPage>
                }
                path="retool/:retool_id"
              />
            </Route>

            <Route
              element={<ErrorPage message="404: Page not found" />}
              path="*"
            />
          </Routes>
        </UnifiedCopilot>
      </CopilotContextProvider>
    </SimpleRoleProvider>
  );
});

const NavigateToUsersPermissions = () => {
  const { orgId, wsId } = useParamsDecode<{ orgId: string; wsId?: string }>();
  const workspaces = useNavigableWorkspaces();
  const workspace =
    workspaces.data?.find((ws) => ws.id === wsId) ??
    workspaces.data?.find((ws) => ws.organization_id === orgId);

  if (!workspace) {
    return <Navigate to={getWelcomeUrl(orgId)} replace />;
  }

  return (
    <Navigate
      to={getUrlToSettingsPage(
        orgId,
        workspace?.id ?? "",
        SettingsSubpages.Users,
      )}
      replace
    />
  );
};

const OrgRoutes = observer(() => {
  const { orgId } = useParamsDecode<{ orgId: string }>();
  const { isLoading, data: organizations } = useAccessibleOrganizations();
  const organization = organizations?.find((org) => org.id === orgId);

  const unauthorizedMessage =
    "You do not have access to the requested organization. Please contact Taktile support if you think this is an error.";

  if ((organizations && !organization) || isLoading) {
    return (
      <Routes>
        <Route
          element={
            isLoading ? (
              <Spinner />
            ) : (
              <ErrorPage message={unauthorizedMessage} />
            )
          }
          path="*"
        />
      </Routes>
    );
  }

  return (
    <SimpleRoleProvider>
      <Routes>
        <Route
          element={
            <ProtectedPage canAccess={(c) => c.usersPermissions.canAccess}>
              <NavigateToUsersPermissions />
            </ProtectedPage>
          }
          path={ORG_ROUTE_CODES.UsersPermissions}
        />
        <Route element={<WelcomePage />} path="welcome" />
        <Route element={<ErrorPage message="404: Page not found" />} path="*" />
      </Routes>
    </SimpleRoleProvider>
  );
});

const AgentRoutesLayout: React.FC = () => {
  const context = useOutletContext();
  return (
    <FloatingWindowsProvider>
      <Outlet context={context} />
    </FloatingWindowsProvider>
  );
};

const RoutesWrapper: React.FC = () => (
  <ErrorBoundary>
    <ThemeProvider>
      <Outlet />
    </ThemeProvider>
  </ErrorBoundary>
);

export const Router = () => {
  const refreshed = useIsUserRefreshed();

  if (!refreshed) {
    return <Spinner />;
  }

  const basename = baseUrl();

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route element={<RoutesWrapper />}>
          <Route element={<GitbookAuthPage />} path="gitbook-auth" />
          <Route element={<PageViewTracker />}>
            <Route element={<PickWorkspace />} path="" />
            <Route element={<UnscopedSession />}>
              <Route element={<OAuthConsentPage />} path="oauth/consent" />
              <Route
                element={
                  <Suspense fallback={<Spinner />}>
                    <AdminPanel />
                  </Suspense>
                }
                path={adminPanelPath}
              />
            </Route>
            <Route element={<WorkspaceScoping />} path="org/:orgId/ws/:wsId/*">
              <Route element={<WorkspaceRoutesContext />}>
                <Route element={<WorkspaceRoutes />} path="*" />
              </Route>
            </Route>
            <Route element={<OrgRoutes />} path="org/:orgId/*" />
            <Route element={<ErrorPage />} path="error" />
            <Route element={<DisplayRedirects />} path="display/:type" />
            <Route
              element={<ErrorPage message="404: Page not found" />}
              path="*"
            />
          </Route>
        </Route>
      </>,
    ),
    {
      basename: basename ? basename + "decide/" : "/decide",
    },
  );

  // Several handlers issue a search-param write and also set state whose
  // effect issues a second one. Wrapping router updates in a transition
  // defers the first navigation past those effects, so the second write
  // derives from the pre-navigation params and drops the first.
  return <RouterProvider router={router} useTransitions={false} />;
};
